import { useState } from 'react';
import { Building2, Edit, Trash2, Plus, CheckCircle, XCircle, Search } from 'lucide-react';
import { useCompanyFlags } from '../../hooks/useFeatureFlags';

interface CompanyOverridesTabProps {
  onRefresh: () => void;
}

export default function CompanyOverridesTab({ onRefresh }: CompanyOverridesTabProps) {
  const { companies, companiesWithFlags, loading, error } = useCompanyFlags();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);

  const filteredCompanies = companiesWithFlags.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Search */}
      <div className="relative overflow-hidden rounded-xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold text-white">Company-Specific Overrides</h3>
              <p className="text-slate-400 mt-1">
                Override global defaults for specific companies
              </p>
            </div>
            <button className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg">
              <Plus className="h-5 w-5 mr-2" />
              Add Override
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search companies..."
              className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-purple-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 p-6 shadow-2xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full transform translate-x-16 -translate-y-16"></div>
          <div className="relative">
            <div className="text-white opacity-70 text-sm font-medium mb-1">Total Companies</div>
            <div className="text-4xl font-bold text-white">{companies.length}</div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-teal-600 p-6 shadow-2xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full transform translate-x-16 -translate-y-16"></div>
          <div className="relative">
            <div className="text-white opacity-70 text-sm font-medium mb-1">With Overrides</div>
            <div className="text-4xl font-bold text-white">{companiesWithFlags.length}</div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 p-6 shadow-2xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full transform translate-x-16 -translate-y-16"></div>
          <div className="relative">
            <div className="text-white opacity-70 text-sm font-medium mb-1">Coverage</div>
            <div className="text-4xl font-bold text-white">
              {companies.length > 0 ? Math.round((companiesWithFlags.length / companies.length) * 100) : 0}%
            </div>
          </div>
        </div>
      </div>

      {/* Companies List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredCompanies.length === 0 ? (
          <div className="col-span-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-400 mb-2">No Companies Found</h3>
            <p className="text-slate-500">
              {searchTerm ? 'No companies match your search' : 'No companies have feature flag overrides yet'}
            </p>
          </div>
        ) : (
          filteredCompanies.map((company) => (
            <div
              key={company.id}
              className="relative overflow-hidden rounded-xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:border-purple-500/50 transition-all cursor-pointer"
              onClick={() => setSelectedCompany(company.id)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white">{company.name}</h4>
                      <p className="text-slate-400 text-sm">{company.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors">
                      <Edit className="w-4 h-4 text-slate-400" />
                    </button>
                    <button className="p-2 hover:bg-red-500/10 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>

                {/* Enabled Features */}
                <div className="space-y-2">
                  {company.enabledFeatures.map((feature: any) => (
                    <div
                      key={feature}
                      className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg"
                    >
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-400 font-medium">{feature}</span>
                    </div>
                  ))}
                  {company.disabledFeatures.map((feature: any) => (
                    <div
                      key={feature}
                      className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg"
                    >
                      <XCircle className="w-4 h-4 text-red-400" />
                      <span className="text-sm text-red-400 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Last Modified */}
                {company.lastModified && (
                  <div className="mt-4 pt-4 border-t border-slate-700/50">
                    <p className="text-slate-500 text-xs">
                      Last modified: {new Date(company.lastModified).toLocaleString()} by {company.modifiedBy || 'Unknown'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
